import { z } from 'zod';

export const AttackSurfaceLicenseSupportSchema = z.enum([
  'NOT_SUPPORTED',
  'SUPPORTED',
  'SUPPORTED_WITH_TORCH',
]);

export type AttackSurfaceLicenseSupport = z.infer<
  typeof AttackSurfaceLicenseSupportSchema
>;

export const LICENSE_TYPES = ['pov', 'customer', 'internal'] as const;

export const LicenseTypeSchema = z.enum(LICENSE_TYPES);

export type LicenseType = (typeof LICENSE_TYPES)[number];

export const ApplicationGenerationLimitsSchema = z.object({
  applicationId: z.string(),
  codeGraphLimit: z.int().min(-1).nullable(),
  signalGenerationLimit: z.int().min(-1).nullable(),
});

export const LicenseResponseSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  name: z.string(),
  licenseType: LicenseTypeSchema.nullable(),
  maxWebApplications: z.int().nullable(),
  maxAiApplications: z.int().nullable(),
  maxExternalNetworks: z.int().nullable(),
  maxInternalNetworks: z.int().nullable(),
  maxExternalNetworkHosts: z.int().nullable(),
  maxInternalNetworkHosts: z.int().nullable(),
  maxTotalAssets: z.int().nullable(),
  codeGraphLimit: z.int().nullable(),
  signalGenerationLimit: z.int().nullable(),
  aiCapacityUnitsPurchased: z.number().nullable(),
  enforceApplicationGenerationLimits: z.boolean(),
  webApplicationSupport: AttackSurfaceLicenseSupportSchema,
  externalNetworkSupport: AttackSurfaceLicenseSupportSchema,
  aiApplicationSupport: AttackSurfaceLicenseSupportSchema,
  internalNetworkSupport: AttackSurfaceLicenseSupportSchema,
  notes: z.string().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const UpsertLicenseBodySchema = z.object({
  name: z.string().min(1),
  maxWebApplications: z.int().positive().nullable().optional(),
  maxAiApplications: z.int().positive().nullable().optional(),
  maxExternalNetworks: z.int().positive().nullable().optional(),
  maxInternalNetworks: z.int().positive().nullable().optional(),
  maxExternalNetworkHosts: z.int().positive().nullable().optional(),
  maxInternalNetworkHosts: z.int().positive().nullable().optional(),
  maxTotalAssets: z.int().positive().nullable().optional(),
  codeGraphLimit: z.int().min(-1).nullable().optional(),
  signalGenerationLimit: z.int().min(-1).nullable().optional(),
  aiCapacityUnitsPurchased: z.number().nonnegative().nullable().optional(),
  enforceApplicationGenerationLimits: z.boolean().optional(),
  applicationGenerationLimits: z
    .array(ApplicationGenerationLimitsSchema)
    .optional(),
  webApplicationSupport: AttackSurfaceLicenseSupportSchema,
  externalNetworkSupport: AttackSurfaceLicenseSupportSchema,
  aiApplicationSupport: AttackSurfaceLicenseSupportSchema,
  internalNetworkSupport: AttackSurfaceLicenseSupportSchema,
  notes: z.string().nullable().optional(),
  expiresAt: z.iso.datetime().nullable().optional(),
  licenseType: LicenseTypeSchema.nullable().optional(),
});

export const CreateTenantLicenseBodySchema = UpsertLicenseBodySchema.extend({
  name: z.string().min(1).optional(),
  licenseType: LicenseTypeSchema,
});

export const ApplicationAssetBreakdownSchema = z.object({
  applicationId: z.string(),
  applicationName: z.string(),
  endpoints: z.number(),
  webPages: z.number(),
  totalAssets: z.number(),
});

export const LicenseUtilizationSchema = z.object({
  license: LicenseResponseSchema.nullable(),
  usage: z.object({
    webApplications: z.number(),
    aiApplications: z.number(),
    externalNetworks: z.number(),
    internalNetworks: z.number(),
    externalNetworkHosts: z.number(),
    internalNetworkHosts: z.number(),
    totalAssets: z.number(),
    perApplication: z.array(ApplicationAssetBreakdownSchema),
  }),
});

export const TenantLicenseSchema = LicenseResponseSchema.omit({
  notes: true,
  aiCapacityUnitsPurchased: true,
  enforceApplicationGenerationLimits: true,
});

export const LicenseAiCapacityModelBurnSchema = z.object({
  modelName: z.string(),
  burned: z.number(),
});

export const LicenseAiCapacitySchema = z.object({
  enabled: z.boolean(),
  purchased: z.number().nullable(),
  burned: z.number(),
  byModel: z.array(LicenseAiCapacityModelBurnSchema),
});

export const TenantLicenseUtilizationSchema = z.object({
  license: TenantLicenseSchema.nullable(),
  usage: LicenseUtilizationSchema.shape.usage,
});
